import { FC } from "react";
import styled from "styled-components";
import { DateTime } from "luxon";

import { TopVideosResource } from "@/types/resources";
import Text from "@/components/Text";
import { Thumbnail } from "@/components/Table";
import { numFormatter } from "@/utils/helpers";
import { Sort } from "./TopThumbnailsSort";

const Card = styled.div`
  align-self: stretch;
  display: flex;
  flex-wrap: wrap;
`;

const Info = styled.div`
  width: 100%;
  padding: 12px;
  background: ${({ theme }) => theme.colors.grayscaleLight};
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-top: 0;
  border-radius: 0 0 4px 4px;
  height: calc(100% - 185px);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const StatsBadge = styled.div`
  background: ${({ theme }) => theme.colors.lightGray};
  border-radius: 4px;
  padding: 4px 8px;
  display: inline-block;
  margin-left: auto;
  margin-top: 12px;
`;

const ChannelName = styled(Text)`
  margin-bottom: 6px;
  display: block;
`;

const StyledThumbnail = styled(Thumbnail)`
  height: 185px;
  width: 100%;
`;

interface Props {
  thumbnail: TopVideosResource;
  activeSort: string;
}

const TopThumbnailsCard: FC<Props> = ({ thumbnail, activeSort }) => {
  const renderStat = () => {
    switch (activeSort) {
      case Sort.PUBLISHED:
        return `Published: ${DateTime.fromISO(
          thumbnail.publishedAt
        ).toLocaleString()}`;
      case Sort.TOTAL_VIEWS:
        return `Total views: ${
          thumbnail.views ? numFormatter(thumbnail.views) : "-"
        }`;
      case Sort.V7:
        return `V7: ${thumbnail.v7 ? numFormatter(thumbnail.v7) : "-"}`;
      case Sort.VPH7:
        return `VPH7: ${thumbnail.vph7 ? numFormatter(thumbnail.vph7) : "-"}`;
      case Sort.V7_INDEX:
        return `V7 index: ${thumbnail.v7Index?.toFixed(2) ?? "-"}`;
      case Sort.R7:
        return `R7: ${thumbnail.r7 ? `$ ${numFormatter(thumbnail.r7)}` : "-"}`;
      default:
        return `Published: ${DateTime.fromISO(
          thumbnail.publishedAt
        ).toLocaleString()}`;
    }
  };

  return (
    <Card>
      <StyledThumbnail src={thumbnail.thumbnail} />
      <Info>
        <div>
          <ChannelName
            href={thumbnail.channelUrl}
            as="a"
            target="_blank"
            $type="extraSmall"
            $color="gray"
            rel="noopener noreferrer"
          >
            {thumbnail.channelName}
          </ChannelName>
          <Text
            as="a"
            href={thumbnail.url}
            target="_blank"
            rel="noopener noreferrer"
            $type="smallHeadline"
          >
            {thumbnail.title}
          </Text>
        </div>
        <StatsBadge>
          <Text $type="extraSmall">{renderStat()}</Text>
        </StatsBadge>
      </Info>
    </Card>
  );
};

export default TopThumbnailsCard;
